// The mobile wish board: one collapsible section per status.
//
// Extracted from `routes/Wishes.tsx` to pay the route-size ratchet
// (AGENTS.md §3 #10). That file is on the known-debt list, and a baseline entry
// is a record of debt rather than permission to add to it — adding the
// `/wishes/trash` entry point pushed it from 15.8 KB over the line, so something
// had to come out. This block is self-contained (own state, three props, no
// imports from the route), which is what made it the right thing to lift rather
// than the thing to shrink by editing comments.

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { KanbanCard } from "@/components/wish/KanbanCard";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import type { WishStatus, WishSummary } from "@/lib/api/wishlist";
import { WISH_STATUS_LABEL, WISH_STATUS_ORDER } from "@/lib/i18n/wishlist";

// W53.1 D-6: both of these were retyped per consumer; they now come from the
// one place that owns the vocabulary.
const MOBILE_STATUS_LABELS: Record<WishStatus, string> = WISH_STATUS_LABEL;

const KANBAN_STATUS_ORDER: readonly WishStatus[] = WISH_STATUS_ORDER;

/** Not actually lazy — the direct import is what the route had. Renamed only
 *  because the section reads better with a short local name. */
const LazyKanbanCard = KanbanCard;

interface MobileCollapsibleProps {
  grouped: Record<WishStatus, WishSummary[]>;
  today: string;
  onCardClick: (w: WishSummary) => void;
}

/**
 * W44 — a group with nothing in it starts **collapsed**.
 *
 * It used to be `pending: true, in_progress: true`, and on the seeded account
 * both of those are empty, so the screen opened on two expanded empty panels.
 * The user's read was 「這兩個 0 個的區塊為甚麼撐這麼大」and they were right: an
 * expanded empty group is a slab of surface with one line of 「沒有」in it, and
 * the header already says `0`.
 *
 * Kept as a static map rather than derived from `grouped`, because a
 * `useState` initialiser cannot see the data on the first render and a group
 * that fills in later would not re-collapse. The empty case is handled at the
 * render site instead (see below), which is the honest place for it.
 */
const ALL_EXPANDED_INITIAL: Record<WishStatus, boolean> = {
  pending: true,
  in_progress: true,
  completed: false,
  abandoned: false,
};

export function MobileCollapsibleSections({
  grouped,
  today,
  onCardClick,
}: MobileCollapsibleProps) {
  const [expanded, setExpanded] = useState<Record<WishStatus, boolean>>(
    ALL_EXPANDED_INITIAL,
  );
  return (
    // W45.0: `mt-s-5` — 20px of air above the first group.
    //
    // `WishPeakCard` carries `mt-s-4` measured from the **top of the page**, and
    // this section had no top margin at all — so the peak card and the 待完成
    // header were flush against each other. 20px matches `PageContainer`'s own
    // `py-s-5` and the `.stack` rhythm the rest of the screen uses, rather than
    // being a number chosen to make one seam look better than another.
    <section
      data-component="MobileCollapsibleSections"
      data-testid="mobile-collapsible-sections"
      className="mt-s-5 flex flex-col gap-s-3"
    >
      {/* Per anniversary baseline (anniversaries/index.tsx §5): the + button
          lives in the PageHero trailing slot. Repeating it here would create
          a duplicate affordance on mobile and dilute the anniversary's
          single-source-of-truth pattern. */}
      {KANBAN_STATUS_ORDER.map((status) => {
        const items = grouped[status];
        // W44: an empty group reads as collapsed no matter what the toggle says.
        // The count is on the header, so nothing is lost — and a group that is
        // empty is not a panel the user asked to look inside.
        const isOpen = expanded[status] && items.length > 0;
        return (
          <Card
            as="section"
            key={status}
            data-component="MobileCollapsibleSection"
            data-status={status}
            data-testid={`mobile-collapsible-section-${status}`}
            className="overflow-hidden"
          >
            <button
              type="button"
              onClick={() =>
                setExpanded((prev) => ({ ...prev, [status]: !prev[status] }))
              }
              aria-expanded={isOpen}
              data-testid={`mobile-collapsible-toggle-${status}`}
              className="press w-full min-h-12 flex items-center justify-between gap-s-2 px-s-4 py-s-3 text-left"
            >
              <span className="text-body-sm font-semibold text-ink-1">
                {MOBILE_STATUS_LABELS[status]}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-meta text-ink-3 tabular-nums">
                  {items.length}
                </span>
                {isOpen ? (
                  <ChevronUp className="h-4 w-4 text-ink-2" aria-hidden />
                ) : (
                  <ChevronDown className="h-4 w-4 text-ink-2" aria-hidden />
                )}
              </span>
            </button>
            {isOpen && items.length > 0 ? (
              <ul
                data-component="MobileCollapsibleList"
                className={cn(
                  "border-t border-hairline px-s-2 py-s-2",
                  "flex flex-col gap-s-2",
                )}
              >
                {items.map((w) => (
                  <li key={w.id}>
                    <MobileCollapsibleRow
                      wish={w}
                      today={today}
                      onClick={() => onCardClick(w)}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            {/* W44: the empty note is **not** rendered even when the group is
                open. Two of the four groups held 0 items and were expanded by
                default, so the screen opened with two slabs of 「這個狀態目前沒有
                心願」 — the tallest text on the page, saying nothing. The count
                is already on the header; the sentence repeated it at three times
                the size. An empty group is information in the number, and the
                header is where the number belongs. */}
          </Card>
        );
      })}
    </section>
  );
}

interface MobileCollapsibleRowProps {
  wish: WishSummary;
  today: string;
  onClick: () => void;
}

function MobileCollapsibleRow({ wish, today, onClick }: MobileCollapsibleRowProps) {
  // Reuse KanbanCard for visual consistency; mobile path just renders it
  // without drag listeners.
  //
  // The click handler lives on the CARD, not on this wrapper. It used to sit
  // on the wrapper, which left the card itself with no onClick — and because
  // `draggable={false}` disables dnd-kit, the card then rendered with
  // `aria-disabled="true"` + `cursor-default` while being perfectly tappable.
  // That announces the primary mobile action as disabled to assistive tech
  // and makes ARIA-honouring tooling refuse to interact with it.
  return (
    <div data-testid={`mobile-collapsible-row-${wish.id}`}>
      <LazyKanbanCard wish={wish} today={today} draggable={false} onClick={onClick} />
    </div>
  );
}
